import axios from 'axios';
import { getAuthToken } from '@redpanda/utils';
import { Role } from '@redpanda/types';
import type { AcceptanceStatus, PMTimeEntry, ProjectProfitability, PMChangeOrder, ChangeOrderStatus, PMProject, PMExpense } from '../types/pm';

const API_URL = process.env.REACT_APP_PM_BACKEND_URL || 'http://localhost:5005/api';
const IDENTITY_API_URL = process.env.REACT_APP_IDENTITY_SERVICE_URL || 'http://localhost:3000/api';
const SUBSCRIPTION_API_URL = process.env.REACT_APP_SUBSCRIPTION_SERVICE_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // 包含 cookies
});

// 添加請求攔截器自動設定 Authorization header
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const identityApi = axios.create({
  baseURL: IDENTITY_API_URL,
  withCredentials: true, // 包含 cookies
});

// 添加 identityApi 的請求攔截器
identityApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const subscriptionApi = axios.create({
  baseURL: SUBSCRIPTION_API_URL,
  withCredentials: true, // 包含 cookies
});

// 添加 subscriptionApi 的請求攔截器
subscriptionApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Project APIs ---
// 列表：分頁＋後端搜尋（R29；回應 { data, pagination }）
export const getProjects = (page = 1, params: { pageSize?: number; search?: string } = {}) =>
  api.get('/projects', { params: { page, pageSize: 20, ...params } });
export const getProjectById = (id: string) => api.get(`/projects/${id}`);
export const createProject = (data: any) => api.post('/projects', data);
export const updateProject = (id: string, data: any) => api.patch(`/projects/${id}`, data);
export const deleteProject = (id: string) => api.delete(`/projects/${id}`);
export const bulkDeleteProjects = (ids: string[]) => api.delete('/projects/bulk', { data: { ids } });
/** 批次封存（archived: true）／恢復（archived: false），照 CRM */
export const setProjectsArchived = (ids: string[], archived: boolean) => api.patch('/projects/bulk', { ids, archived });
export const getArchivedProjects = (page = 1, params: { pageSize?: number; search?: string } = {}) =>
  api.get('/projects', { params: { page, pageSize: 20, archived: true, ...params } });
export const cloneProject = (id: string) => api.post(`/projects/${id}/clone`);

// --- Dashboard APIs ---
export const getDashboardStats = () => api.get('/dashboard/stats');

// --- Task APIs ---
export const createTask = (projectId: string, data: any) => api.post(`/projects/${projectId}/tasks`, data);
export const updateTask = (id: string, data: any) => api.patch(`/tasks/${id}`, data);
export const deleteTask = (id: string) => api.delete(`/tasks/${id}`);
export const getTaskActivities = (id: string) => api.get(`/tasks/${id}/activities`);
export const addTaskComment = (id: string, body: string) => api.post(`/tasks/${id}/comments`, { body });

// --- 通知 ---
/** 通知：一次 30 則；before＝目前最舊一則的時間（載入更多） */
export const getNotifications = (before?: string) => api.get('/notifications', { params: before ? { before } : undefined });
/** 標為已讀；沒帶 ids＝全部 */
export const markNotificationsRead = (ids?: string[]) => api.patch('/notifications', { ids });

// --- Package APIs ---
export const createPackage = (projectId: string, data: any) => api.post(`/projects/${projectId}/packages`, data);
export const updatePackage = (id: string, data: any) => api.patch(`/packages/${id}`, data);
export const deletePackage = (id: string) => api.delete(`/packages/${id}`);

// --- Milestone APIs ---
export const createMilestone = (projectId: string, data: any) => api.post(`/projects/${projectId}/milestones`, data);
export const updateMilestone = (id: string, data: any) => api.patch(`/milestones/${id}`, data);
export const deleteMilestone = (id: string) => api.delete(`/milestones/${id}`);
export const reorderMilestones = (projectId: string, data: any) => api.patch(`/projects/${projectId}/milestones/reorder`, data);

// --- Requirement APIs ---
export const createRequirement = (projectId: string, data: any) => api.post(`/projects/${projectId}/requirements`, data);
export const updateRequirement = (id: string, data: any) => api.patch(`/requirements/${id}`, data);
export const deleteRequirement = (id: string) => api.delete(`/requirements/${id}`);

// --- Payment APIs ---
export const createPayment = (projectId: string, data: any) => api.post(`/projects/${projectId}/payments`, data);
export const updatePayment = (id: string, data: any) => api.patch(`/payments/${id}`, data);
export const deletePayment = (id: string) => api.delete(`/payments/${id}`);

// --- Admin & User Management APIs (using identity-service) ---
export const getApprovedUsers = () => identityApi.get('/admin/users/approved');
export const getPendingUsers = () => identityApi.get('/admin/users/pending');

// 更新用戶角色 (RESTful: PATCH /users/:id/role)
export const updateUserRole = (userId: string, role: Role) =>
  identityApi.patch(`/admin/users/${userId}/role`, { role });

// 刪除用戶 (RESTful: DELETE /users/:id)
export const deleteUser = (userId: string) => identityApi.delete(`/admin/users/${userId}`);

// Legacy API (向後相容) - 建議使用上面的 RESTful API
export const approveUser = (userId: string, role: Role, config?: { allowedSystems?: string[] }) =>
  identityApi.post(`/admin/users/${userId}/approve`, { role, ...config });
export const rejectUser = (userId: string) => identityApi.post(`/admin/users/${userId}/reject`);

// --- Data IO APIs ---
export const exportToExcel = (sheets: string[], language?: string) => {
  return api.post('/data/export/excel', { sheets, language }, {
    responseType: 'blob',
  });
};

export const importFromExcel = (file: File, language?: string, onUploadProgress?: (progressEvent: any) => void) => {
  const formData = new FormData();
  formData.append('file', file);
  if (language) {
    formData.append('language', language);
  }
  return api.post('/data/import/excel', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress,
  });
};

// --- 選單資料 ---
export interface OrgMember { id: string; name: string | null; email: string }
export interface CrmCustomer { id: string; name: string; company: string | null; archived: boolean }
/** 組織內能用 PM 的成員（任務負責人選單） */
export const getOrgMembers = () => identityApi.get<OrgMember[]>('/organization/members', { params: { system: 'PM' } });
/** 這個組織在 CRM 的客戶（PM 後端代查，使用者不需要 CRM 權限） */
export const getCrmCustomers = () => api.get<CrmCustomer[]>('/crm/customers');

// --- 應收帳款 ---
export const getReceivables = () => api.get('/receivables');
/** 依客戶：帳齡分析＋平均收款天數 */
export const getReceivableCustomers = () => api.get('/receivables/customers');
/** 已收款：跨專案收款記錄（收款日區間 YYYY-MM-DD，不給＝全部） */
export const getReceivedPayments = (query: { from?: string; to?: string; page: number }) => api.get('/payments', { params: query });
// 請款記錄（:id＝應收的里程碑／分包／專案 id）
export const setReceivableInvoice = (id: string, data: { invoicedAt: string; dueDate: string }) => api.put(`/receivables/${id}/invoice`, data);
export const clearReceivableInvoice = (id: string) => api.delete(`/receivables/${id}/invoice`);

// --- Project Limit APIs (using subscription-service) ---
export const getProjectLimitInfo = () => {
  return subscriptionApi.get(`/subscriptions/project-limit`);
};

// 認證錯誤處理攔截器
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      if (status === 401 || status === 403) {
        if (data && typeof data.error === 'string' &&
           (data.error.includes('Account not found or deleted.') || data.error.includes('Account status for user'))) {
          console.warn('帳號已被刪除或停用,自動登出。');
          window.location.href = '/login';
        } else if ((status === 401 && data && typeof data.error === 'string' && data.error.includes('jwt expired')) ||
                   (status === 403 && data && typeof data.error === 'string' && (data.error.includes('Token is outdated. Please log in again.') || data.error.includes('Token is outdated due to role change')))) {
          console.warn('Token 已過期或權限已變更,自動登出。');
          window.location.href = '/login';
        } else if (status === 401) {
          console.warn('未授權錯誤,自動登出。');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
export interface CrmQuote {
  id: string;
  title: string;
  totalAmount: number;
  status: string;
  customerId: string;
  createdAt: string;
  customer: { name: string; company: string | null } | null;
  /** 已用這張報價建立的 PM 專案 */
  projectId: string | null;
}
/** 已成交的 CRM 報價（新增專案「從報價帶入」用） */
export const getCrmQuotes = () => api.get<CrmQuote[]>('/crm/quotes');

// --- 客戶驗收連結 ---
export const enableShareLink = (projectId: string) => api.post<{ shareToken: string }>(`/projects/${projectId}/share-link`);
export const disableShareLink = (projectId: string) => api.delete(`/projects/${projectId}/share-link`);
// 呆帳（管理員）：列入／取消，回傳更新後的專案
export const setProjectBadDebt = (projectId: string, note: string | null) => api.post<PMProject>(`/projects/${projectId}/bad-debt`, { note });
export const clearProjectBadDebt = (projectId: string) => api.delete<PMProject>(`/projects/${projectId}/bad-debt`);

/** 公開端點（客戶免登入）：不用 api 實例，避免帶入登入 token 與 401 轉址 */
const publicApi = axios.create({ baseURL: `${API_URL}/public`, headers: { 'Content-Type': 'application/json' } });
export interface PublicMilestone {
  id: string;
  name: string;
  description: string | null;
  dueDate: string | null;
  completedDate: string | null;
  status: string;
  acceptanceStatus: AcceptanceStatus | null;
  acceptanceBy: string | null;
  acceptanceNote: string | null;
  acceptanceAt: string | null;
}
export type PublicChangeOrder = Pick<PMChangeOrder, 'id' | 'title' | 'description' | 'amount' | 'estimatedHours' | 'status' | 'decidedBy' | 'decisionNote' | 'decidedAt'>;
export interface PublicAcceptance {
  project: { name: string; status: string; expectedDeliveryDate: string | null };
  milestones: PublicMilestone[];
  changeOrders: PublicChangeOrder[];
}
export const getPublicAcceptance = (token: string) => publicApi.get<PublicAcceptance>(`/acceptance/${encodeURIComponent(token)}`);
export const respondToMilestone = (token: string, milestoneId: string, data: { decision: AcceptanceStatus; name: string; note?: string }) =>
  publicApi.post<Pick<PublicMilestone, 'id' | 'acceptanceStatus' | 'acceptanceBy' | 'acceptanceNote' | 'acceptanceAt'>>(
    `/acceptance/${encodeURIComponent(token)}/milestones/${encodeURIComponent(milestoneId)}/responses`,
    data
  );

// --- 工時記錄與毛利 ---
export interface TimeEntryQuery { projectId?: string; taskId?: string; userId?: string; from?: string; to?: string }
interface TimeEntryInput { projectId?: string; userId?: string; taskId?: string | null; date?: string; hours?: number; note?: string | null }
export const getTimeEntries = (params: TimeEntryQuery) => api.get<PMTimeEntry[]>('/time-entries', { params });
export interface Paginated<T> {
  data: T[];
  pagination: { totalItems: number; totalPages: number; currentPage: number; pageSize: number };
}
/** 專案詳情「工時」分頁：分頁抓這個專案全部成員的記錄 */
export const getProjectTimeEntries = (projectId: string, page: number) =>
  api.get<Paginated<PMTimeEntry>>(`/projects/${projectId}/time-entries`, { params: { page } });
export const createTimeEntry = (data: TimeEntryInput) => api.post<PMTimeEntry>('/time-entries', data);
export const updateTimeEntry = (id: string, data: TimeEntryInput) => api.patch<PMTimeEntry>(`/time-entries/${id}`, data);
export const deleteTimeEntry = (id: string) => api.delete(`/time-entries/${id}`);
/** 成員每小時成本（管理員） */
export const getMemberRates = () => api.get<{ userId: string; hourlyCost: number }[]>('/member-rates');
export const setMemberRate = (userId: string, hourlyCost: number) => api.put(`/member-rates/${userId}`, { hourlyCost });
export const getProjectProfitability = (projectId: string) => api.get<ProjectProfitability>(`/projects/${projectId}/profitability`);

// 外部成本、成本預算（管理員）
export type ExpensePayload = Partial<Pick<PMExpense, 'packageId' | 'expenseDate' | 'amount' | 'category' | 'vendor' | 'description'>>;
export const getExpenses = (projectId: string) => api.get<PMExpense[]>(`/projects/${projectId}/expenses`);
export const createExpense = (projectId: string, data: ExpensePayload) => api.post<PMExpense>(`/projects/${projectId}/expenses`, data);
export const updateExpense = (id: string, data: ExpensePayload) => api.patch<PMExpense>(`/expenses/${id}`, data);
export const deleteExpense = (id: string) => api.delete(`/expenses/${id}`);
export interface ProfitabilityRow {
  projectId: string; name: string; customerId: string | null;
  revenue: number; laborCost: number; expenseCost: number; totalCost: number;
  margin: number; marginRate: number | null; costUsage: number | null;
}
/** 跨專案利潤報表（管理員） */
export const getProfitabilityReport = () => api.get<ProfitabilityRow[]>('/reports/profitability');
/** 定期收費（管理員）：設定後立刻補建已到期的各期分包；DELETE 停止（已建立的分包保留） */
export const setRecurringBilling = (projectId: string, data: { amount: number; interval: string; name: string; startDate: string; endDate: string | null }) =>
  api.put<Partial<PMProject>>(`/projects/${projectId}/recurring-billing`, data);
export const clearRecurringBilling = (projectId: string) => api.delete(`/projects/${projectId}/recurring-billing`);
export const setCostBudget = (projectId: string, costBudget: number | null) => api.put(`/projects/${projectId}/cost-budget`, { costBudget });
/** 任務選單用（工時對話框選專案後載入） */
export const getProjectTasks = (projectId: string) => api.get<{ tasks: { id: string; title: string; status: string }[] }>(`/projects/${projectId}/tasks`);

// --- 變更單 ---
interface ChangeOrderInput { title?: string; description?: string | null; amount?: number; estimatedHours?: number | null }
export type ChangeOrderDecision = Exclude<ChangeOrderStatus, 'PENDING'>;
export const getChangeOrders = (projectId: string) => api.get<PMChangeOrder[]>(`/projects/${projectId}/change-orders`);
export const createChangeOrder = (projectId: string, data: ChangeOrderInput) => api.post<PMChangeOrder>(`/projects/${projectId}/change-orders`, data);
export const updateChangeOrder = (id: string, data: ChangeOrderInput) => api.patch<PMChangeOrder>(`/change-orders/${id}`, data);
export const deleteChangeOrder = (id: string) => api.delete(`/change-orders/${id}`);
/** 團隊標記核准／拒絕（核准時後端建立分包） */
export const decideChangeOrder = (id: string, status: ChangeOrderDecision, note?: string) =>
  api.patch<PMChangeOrder>(`/change-orders/${id}/status`, { status, note });
/** 客戶從驗收連結核准／拒絕 */
export const respondToChangeOrder = (token: string, orderId: string, data: { decision: ChangeOrderDecision; name: string; note?: string }) =>
  publicApi.post<Pick<PMChangeOrder, 'id' | 'status' | 'decidedBy' | 'decisionNote' | 'decidedAt'>>(
    `/acceptance/${encodeURIComponent(token)}/change-orders/${encodeURIComponent(orderId)}/responses`,
    data
  );
